import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import { AGGREGATE_NAMES, LOAN_REQUEST_COMMANDS } from 'cpool-constants';
import type { SchemaLoanRequestResolver_GetAcceptedOffers } from 'cpool-tables/backend-resolvers';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { APICommunicator } from '~/api/APICommunicator.ts';
import { setQueryPosition } from '~/store/queryPositionStore.ts';
import { appendPositionToQueryKey } from '~/utils/appendPositionToQueryKey.ts';
import { queryKeys } from '~/utils/queryKeys.ts';

type Props = {
    aggregateId: string;
    id: string;
    favored: boolean;
};

export const useFavoritismOffers = ({ aggregateId, favored, id }: Props) => {
    const search = useSearch({ strict: false });
    const offerFilter = 'offerFilter' in search ? search.offerFilter : {};
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const queryKey = queryKeys.getAcceptedOffers(aggregateId, offerFilter);

    return useMutation({
        mutationFn: async () => {
            return APICommunicator.sendCommand({
                aggregateId,
                aggregateName: AGGREGATE_NAMES.LOAN_REQUEST,
                commandName: LOAN_REQUEST_COMMANDS.changeOfferFavorability,
                payload: {
                    favored,
                    id,
                },
            });
        },
        onError: () => {
            toast.error(t('OFFER_MANAGEMENT.FAVORITISM_CHANGED_ERROR'));
        },
        onMutate: () => {
            queryClient.setQueryData(
                appendPositionToQueryKey.current(queryKey),
                (currentData: SchemaLoanRequestResolver_GetAcceptedOffers) => {
                    if (Array.isArray(currentData)) {
                        const offer = currentData.find((entry) => entry.id === id);
                        if (offer) offer.favored = favored;
                    }
                    return currentData;
                },
            );
            // The optimistic update re-renders the row with the inverted prop, so keep the requested value
            return { favored };
        },
        onSettled: (data) => {
            queryClient.invalidateQueries({ queryKey: appendPositionToQueryKey.custom(queryKey, data?.position) });
        },
        onSuccess: (data, _variables, context) => {
            toast.success(
                t(
                    context.favored
                        ? 'OFFER_MANAGEMENT.TOASTS.FAVORED_SUCCESS'
                        : 'OFFER_MANAGEMENT.TOASTS.UNFAVORED_SUCCESS',
                ),
            );
            setQueryPosition(JSON.stringify(queryKey), data.position);
        },
    });
};
